import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Crown, Check } from "lucide-react";

interface UpgradePromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  feature?: string;
  planName?: string;
  planFeatures?: string[];
}

export function UpgradePromptDialog({
  isOpen,
  onClose,
  title = "Upgrade to Professional",
  description = "Unlock the full potential of ManageClean with our Professional plan.",
  feature,
  planName = "Professional",
  planFeatures = [
    "Unlimited Bids & Proposals",
    "Save unlimited templates",
    "Advanced Analytics"
  ],
}: UpgradePromptDialogProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate("/settings/billing?tab=plans");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <div className="flex flex-col gap-2">
          <DialogHeader className="pb-2 text-center">
          <div className="mx-auto mb-2 w-fit rounded-full bg-amber-100 p-3">
            <Crown className="h-6 w-6 text-amber-600" />
          </div>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="pt-2">
            {feature ? (
              <span className="mb-1 block font-semibold text-foreground">
                {feature} is a premium feature.
              </span>
            ) : null}
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="my-2 shrink-0 space-y-2 rounded-lg bg-slate-50 p-4 text-sm">
          <p className="font-medium text-slate-900">{planName} Plan Includes:</p>
          {planFeatures.map((feat, index) => (
            <div key={index} className="flex items-center gap-2 text-slate-700">
              <Check className="h-4 w-4 text-green-600" />
              <span>{feat}</span>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            onClick={handleUpgrade}
            className="w-full bg-amber-600 text-white hover:bg-amber-700"
          >
            Check Upgrade Options
          </Button>
          <Button variant="ghost" onClick={onClose} className="w-full">
            Maybe Later
          </Button>
        </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
